import Cookie from 'js-cookie'
import { browserLanguage } from './constants'

// The platform session lives in ONE cookie holding the api_key together with how the user signed in:
//   { v: 1, token, method: 'credentials' }
//   { v: 1, token, method: 'azure', oid?, name?, upn?, mail? }
// The Azure fields are exactly what the Segment identify and the LAYI login headers need (the Entra
// object id, display name, UPN and — once MS Graph resolves it — the mailbox address). Never the
// Graph access token. It replaced the bare `token` cookie: a browser still holding `token` starts
// logged out once and signs in again, so every live session carries its sign-in method.
export const SESSION_COOKIE = 'layers_session'
export const LEGACY_TOKEN_COOKIE = 'token'
export const SESSION_VERSION = 1

export const AUTH_METHODS = Object.freeze({ CREDENTIALS: 'credentials', AZURE: 'azure' })

// Azure sessions last one day (a daily Entra sign-in); credentials a week.
export const SESSION_EXPIRY_DAYS = Object.freeze({ [AUTH_METHODS.CREDENTIALS]: 7, [AUTH_METHODS.AZURE]: 1 })

const AZURE_FIELDS = ['oid', 'name', 'upn', 'mail']
// Travels on every same-origin request: a value this long is not a name, it is cookie bloat.
const MAX_FIELD_LENGTH = 256

const cleanField = value => {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed && trimmed.length <= MAX_FIELD_LENGTH ? trimmed : undefined
}

// The sign-in description this value names, or null. Credentials sessions never carry Azure fields;
// unknown or malformed Azure fields are dropped, never truncated.
export const normalizeAuth = auth => {
  const method = auth && auth.method
  if (method === AUTH_METHODS.CREDENTIALS) return Object.freeze({ method })
  if (method !== AUTH_METHODS.AZURE) return null
  const fields = AZURE_FIELDS.reduce((acc, field) => {
    const value = cleanField(auth[field])
    return value ? { ...acc, [field]: value } : acc
  }, {})
  return Object.freeze({ method, ...fields })
}

// { token, auth } from a raw cookie value, or null for anything that is not a well-formed session.
export const parseSession = raw => {
  if (typeof raw !== 'string') return null
  let data
  try {
    data = JSON.parse(raw)
  } catch (e) {
    return null
  }
  if (!data || data.v !== SESSION_VERSION || typeof data.token !== 'string' || !data.token) return null
  const auth = normalizeAuth(data)
  return auth ? { token: data.token, auth } : null
}

export const serializeSession = ({ token, auth }) => JSON.stringify({ v: SESSION_VERSION, token, ...auth })

const DEFAULT_AUTH = Object.freeze({ method: AUTH_METHODS.CREDENTIALS })

class UserStorageData {
  constructor() {
    this._listeners = new Set()
    this._expires = null
    const session = this._readSession()
    this._token = session ? session.token : null
    this._auth = session ? session.auth : null
    this._locale = Cookie.get('locale') || browserLanguage
  }

  _readSession() {
    // A pre-session-cookie login (or one written by an old bundle still open in another tab):
    // its sign-in method is unknown, so neither cookie is trusted — sign in again, once.
    if (Cookie.get(LEGACY_TOKEN_COOKIE) !== undefined) {
      Cookie.remove(LEGACY_TOKEN_COOKIE)
      Cookie.remove(SESSION_COOKIE)
      return null
    }
    const raw = Cookie.get(SESSION_COOKIE)
    const session = parseSession(raw)
    if (raw !== undefined && !session) Cookie.remove(SESSION_COOKIE)
    return session
  }

  _writeSession() {
    Cookie.set(SESSION_COOKIE, serializeSession({ token: this._token, auth: this._auth }), { expires: this._expires })
  }

  _notify() {
    this._listeners.forEach(listener => listener())
  }

  get hasToken() {
    return !!this._token
  }

  get hasLocale() {
    return !!this._locale
  }

  // `auth` says how the user signed in; omitted means credentials (password, TokenLogin).
  setToken(token, auth = DEFAULT_AUTH) {
    if (!token) {
      this.removeToken()
      return
    }
    this._token = token
    this._auth = normalizeAuth(auth) || DEFAULT_AUTH
    const days = SESSION_EXPIRY_DAYS[this._auth.method]
    this._expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000)
    this._writeSession()
    this._notify()
  }

  removeToken() {
    Cookie.remove(SESSION_COOKIE)
    // Already logged out: nothing changed, nobody to re-render (Login calls this while rendering).
    if (!this._token && !this._auth) return
    this._token = null
    this._auth = null
    this._expires = null
    this._notify()
  }

  setLocale(locale) {
    this._locale = locale

    Cookie.set('locale', this._locale, { expires: 365 })
  }

  get locale() {
    return this._locale
  }

  get API_KEY() {
    return this.getToken()
  }

  get token() {
    return this.getToken()
  }

  getToken = () => {
    return this._token
  }

  // How the current session signed in ({ method, oid?, name?, upn?, mail? }, frozen), or null when
  // logged out. The same object is returned until the session changes (useSyncExternalStore-safe).
  getAuth = () => {
    return this._auth
  }

  // Merges Azure fields into the current session (e.g. the Graph mailbox address resolved after
  // login). The sign-in method cannot change here — that is a new login (setToken). No-op without a
  // session. Keeps the expiry set at login in this page; after a reload it restarts the method's term.
  setAuth = patch => {
    if (!this._token || !this._auth) return false
    const next = normalizeAuth({ ...this._auth, ...patch, method: this._auth.method })
    this._auth = next
    if (!this._expires) {
      const days = SESSION_EXPIRY_DAYS[next.method]
      this._expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000)
    }
    this._writeSession()
    this._notify()
    return true
  }

  subscribe = listener => {
    this._listeners.add(listener)
    return () => this._listeners.delete(listener)
  }
}

export const createUserStorageData = () => new UserStorageData()

const userInstance = createUserStorageData()

export default userInstance

export const { getToken, getAuth, setAuth, subscribe } = userInstance
